import type { LovOption, LovTagType } from './types'

const TAG_TYPES = new Set([
  'primary',
  'success',
  'info',
  'warning',
  'danger',
])

/** 后端 tagType 编码 → Element Plus Tag type */
export const DEFAULT_LOV_TAG_CODE_MAP: Record<string, LovTagType> = {
  TAG01: 'primary',
  TAG02: 'success',
  TAG03: 'info',
  TAG04: 'warning',
  TAG05: 'danger',
}

function resolveTagType(row: Record<string, unknown>): LovTagType | undefined {
  const raw = row.tagType ?? row.tag_type
  if (typeof raw !== 'string' || !raw) {
    return undefined
  }
  if (TAG_TYPES.has(raw)) {
    return raw as LovTagType
  }
  return DEFAULT_LOV_TAG_CODE_MAP[raw]
}

const DEFAULT_VALUE_KEYS = ['lovValue', 'value', 'code', 'id'] as const
const DEFAULT_LABEL_KEYS = ['lovTitle', 'label', 'name', 'text'] as const

function pickField(
  row: Record<string, unknown>,
  primary: string | undefined,
  fallbacks: readonly string[]
): string {
  if (primary && row[primary] != null && row[primary] !== '') {
    return String(row[primary])
  }
  for (const key of fallbacks) {
    if (row[key] != null && row[key] !== '') {
      return String(row[key])
    }
  }
  return ''
}

/** `{ 1: '无效', 2: '有效' }` -> LovOption[] */
export function normalizeLovMapRecord(
  map: Record<string, unknown>
): LovOption[] {
  return Object.entries(map)
    .map(([value, label]) => {
      if (value === '') return null
      return {
        value: String(value),
        label: label == null || label === '' ? String(value) : String(label),
        disabled: false,
      } satisfies LovOption
    })
    .filter((item): item is LovOption => item != null)
}

function normalizeLovBatchEntry(value: unknown): LovOption[] {
  if (Array.isArray(value)) {
    return normalizeLovResponse(value)
  }
  if (value && typeof value === 'object') {
    return normalizeLovMapRecord(value as Record<string, unknown>)
  }
  return []
}

/**
 * 批量响应：CommonResult<Map<String, List<LovDO>>>
 * - List<LovDO>: `{ DICT_GENDER: [{ lovValue, lovTitle, tagType }] }`
 * - 兼容 Map 文案: `{ status: { 1: '无效' } }`
 */
export function normalizeLovBatchResponse(
  data: unknown
): Record<string, LovOption[]> {
  let raw: unknown = data
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const wrapper = data as Record<string, unknown>
    if (
      wrapper.data &&
      typeof wrapper.data === 'object' &&
      !Array.isArray(wrapper.data)
    ) {
      raw = wrapper.data
    }
  }

  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {}
  }

  const result: Record<string, LovOption[]> = {}
  for (const [lovType, entry] of Object.entries(raw as Record<string, unknown>)) {
    const options = normalizeLovBatchEntry(entry)
    if (options.length) {
      result[lovType] = options
    }
  }
  return result
}

/** 从 getLovList 响应中提取指定 lovType 的 LovDO 行（含 lovTitle / lovValue） */
export function extractLovListRows(
  data: unknown,
  lovType: string
): Record<string, unknown>[] {
  let raw: unknown = data
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const wrapper = data as Record<string, unknown>
    if (wrapper.data != null) {
      raw = wrapper.data
    }
  }

  // CommonResult<List<LovDO>>：data 为扁平数组，每行带 lovType / lovValue / lovTitle
  if (Array.isArray(raw)) {
    return raw.filter((item): item is Record<string, unknown> => {
      if (item == null || typeof item !== 'object') {
        return false
      }
      const row = item as Record<string, unknown>
      const rowType = row.lovType
      return rowType == null || rowType === '' || rowType === lovType
    })
  }

  // CommonResult<Map<lovType, List<LovDO>>>
  if (!raw || typeof raw !== 'object') {
    return []
  }
  const entry = (raw as Record<string, unknown>)[lovType]
  if (!Array.isArray(entry)) {
    return []
  }
  return entry.filter(
    (item): item is Record<string, unknown> =>
      item != null && typeof item === 'object'
  )
}

export function normalizeLovResponse(
  data: unknown,
  valueField?: string,
  labelField?: string
): LovOption[] {
  let list: unknown[] = []
  if (Array.isArray(data)) {
    list = data
  } else if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>
    if (Array.isArray(o.data)) list = o.data
    else if (Array.isArray(o.list)) list = o.list
    else if (Array.isArray(o.records)) list = o.records
    else if (Array.isArray(o.rows)) list = o.rows
  }

  return list
    .map((item) => {
      if (item == null || typeof item !== 'object') return null
      const row = item as Record<string, unknown>
      const value = pickField(row, valueField, DEFAULT_VALUE_KEYS)
      const label = pickField(row, labelField, DEFAULT_LABEL_KEYS) || value
      if (!value) return null
      return {
        value,
        label,
        disabled: row.disabled === true,
        tagType: resolveTagType(row),
      } satisfies LovOption
    })
    .filter((item): item is LovOption => item != null)
}
